import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { Toggle } from 'components/Toggle/Toggle';
import { constFalse, constNull } from 'fp-ts/lib/function';
import {
  type ComponentType,
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select, {
  components,
  type ControlProps,
  type OptionProps,
} from 'react-select';
import { GraphToolbarButton } from './GraphToolbarButton';

interface Option {
  disabled?: boolean;
  label: ReactNode;
  onClick: () => void;
  toggled?: boolean;
  tooltip?: string;
}

interface Props {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  disabledTitle?: string;
  options: Option[];
  title?: string;
}

interface ResolvedOption extends Option {
  value: number;
  toggle: ReactNode;
}

const ControlComponent: ComponentType<ControlProps<ResolvedOption>> = ({
  children,
  innerProps,
  innerRef,
}) => (
  <div {...innerProps} ref={innerRef}>
    {children}
  </div>
);

const OptionComponent: ComponentType<OptionProps<ResolvedOption>> = ({
  children,
  ...otherProps
}) => (
  <components.Option {...otherProps}>
    {children}
    {otherProps.data.toggle}
  </components.Option>
);

// TODO: This should be replaced by a custom, reusable dropdown UI component, preferrably
// one that doesn't use `react-select`.
export const GraphToolbarDropdown: FC<Props> = (props) => {
  const selectRef = useRef<any>(null);
  const options = props.options.map((option, index) => ({
    ...option,
    label:
      // HACK: `span` must be used so the tooltip can latch onto something.
      option.tooltip ? (
        <Tooltip content={option.tooltip}>
          <span>{option.label}</span>
        </Tooltip>
      ) : (
        option.label
      ),
    value: index,
    toggle: option.toggled !== undefined && (
      <Toggle
        value={option.toggled}
        onChange={option.onClick}
        hideStateMessage
      />
    ),
  }));

  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  // TOP OFFSET is top offset to align the dropdown with menu items by top boarding
  const TOP_OFFSET = 14;
  const [topPosition, setTopPosition] = useState<number>(-TOP_OFFSET);

  // biome-ignore lint/correctness/useExhaustiveDependencies(menuIsOpen): toggled when menu is open
  useEffect(() => {
    const menuListRef = selectRef.current?.menuListRef;
    const { bottom } = (
      menuListRef as HTMLElement | null
    )?.getBoundingClientRect() || { bottom: 0 };

    const isOverflow = window.innerHeight - bottom < 0;
    setTopPosition(
      isOverflow ? window.innerHeight - bottom - TOP_OFFSET : -TOP_OFFSET,
    );
  }, [menuIsOpen]);

  const disabled = useMemo(
    () => props.disabled || props.options.every((option) => option.disabled),
    [props.options, props.disabled],
  );

  const handleClose = useStableCallback(() => {
    setMenuIsOpen(false);
  });

  useEffect(() => {
    if (disabled && menuIsOpen) {
      setMenuIsOpen(false);
    }
  }, [disabled, menuIsOpen]);

  const wrapperRef = useClickAway<HTMLDivElement>(handleClose);

  return (
    <div ref={wrapperRef}>
      <Select
        ref={selectRef}
        closeMenuOnSelect={false}
        components={{
          Control: ControlComponent,
          DropdownIndicator: constNull,
          Option: OptionComponent,
          Placeholder: constNull,
          IndicatorSeparator: constNull,
          Input: constNull,
          SingleValue: constNull,
        }}
        isOptionDisabled={(option) => !!option.disabled}
        isOptionSelected={constFalse}
        isSearchable={false}
        menuIsOpen={menuIsOpen}
        onChange={(option) => {
          props.options[(option as ResolvedOption).value]?.onClick();

          if ((option as ResolvedOption).toggled === undefined) {
            setMenuIsOpen(false);
          }
        }}
        onMenuClose={() => {
          setMenuIsOpen(false);
        }}
        options={options}
        styles={{
          container: (styles) => ({
            ...styles,
            alignSelf: 'middle',
            width: '40px',
            display: 'flex',
            justifyContent: 'center',
          }),
          menu: (styles) => ({
            ...styles,
            width: '260px',
            top: topPosition,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            boxShadow: 'none',
            border: '1px solid #D6D6D6',
            right: '40px',
          }),
          menuList: (styles) => ({
            ...styles,
            border: 'none',
          }),
          option: (styles, { isDisabled }) => ({
            ...styles,
            cursor: isDisabled ? styles.cursor : 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
          }),
          valueContainer: (styles) => ({
            ...styles,
            position: 'absolute',
            pointerEvents: 'none',
          }),
        }}
      />
      <GraphToolbarButton
        className={props.className}
        disabled={disabled}
        disabledTitle={props.disabledTitle}
        onClick={() => {
          setMenuIsOpen(!menuIsOpen);
        }}
        title={props.title}
      >
        {props.children}
      </GraphToolbarButton>
    </div>
  );
};
